/**
 * „Kto ma którą" — the clients × surveys matrix, as KANON `02-kto-ma-ktora.html` draws it.
 *
 * ── ONE read builds the whole grid ───────────────────────────────────────────────────────────
 * `GET /survey/templates` carries `assignedCustomerIds` on every row, so every tick in the grid is
 * already on the wire when the list answers. There is no read per template and there must never be
 * one: fifteen surveys would be fifteen requests for a fact one response already holds.
 *
 * ── The footer counts the TICKS, not the backend's figure ────────────────────────────────────
 * A column's „n z N" is the number of ROWS in this matrix whose id is in that column's set, over the
 * number of rows. `assignedClientCount` is deliberately not used for it: the count includes people
 * the roster does not return as rows — an archived client keeps their assignment — and a footer
 * reading „9/8" under eight visible ticks is a figure that argues with the grid above it. The card
 * beside the matrix states the backend's own figure separately, where it is a fact about the survey
 * rather than about this table.
 *
 * ── The intake is NOT a column ──────────────────────────────────────────────────────────────
 * It is assigned by onboarding, not by the trainer: a client fills it at their first sign-in and no
 * cell in this grid could be pressed to change that. Drawing it as a column would be drawing
 * fifteen checkboxes that refuse. The footer's „wstępną ma 7 z 8" tally went on 4 November 2026
 * (owner): the corner states the client count alone, and the Ankiety tab describes the intake.
 *
 * ── A PAUSED column takes an unassignment and refuses an assignment ─────────────────────────
 * The asymmetry is the backend's, not a choice made here. `SurveyTemplateProcessor.AssignCheckInAsync`
 * refuses a paused check-in outright — „A paused check-in cannot be assigned; resume it first." —
 * while `UnassignCheckInAsync` carries no such gate, and says so in a comment of its own: a client
 * must be removable from a paused check-in too. So an EMPTY cell in a paused column is refused here,
 * before the request, with the reason rendered beside it; a TICKED one still works, because taking
 * somebody off a paused survey is a reasonable thing to want and the server takes it.
 *
 * An earlier revision of this file said assigning into a paused column was allowed and the footer
 * note told the trainer it would „start working when you resume it". It did not: the tick stayed
 * off and a 400 landed beside that sentence. `cellRefusal` is where the truth now lives.
 *
 * ── A column with nobody in it still exists ──────────────────────────────────────────────────
 * An empty set is an empty column, never a missing one: a survey the trainer has assigned to nobody
 * is precisely the survey they came to this screen to assign, and a grid that hid it would hide the
 * one thing it is for.
 *
 * No React, no DOM, no query hook, no i18next: `t` arrives as a parameter.
 */

import type { ClientFace } from './clientFaces';
import { cadenceLabel, type SurveyGroups, type SurveyRow, type TranslateKey } from './surveyList';

/** One row: a person, drawn in the pinned left column. */
export interface MatrixRow {
  readonly customerId: number;
  readonly name: string;
  readonly initials: string;
  readonly avatarUrl: string | null;
}

/** One column: a check-in, its head, and who holds it. */
export interface MatrixColumn {
  readonly id: number;
  readonly title: string;
  /** „co tydz · pon", with „ · wstrzymana" already folded in when the survey is paused. */
  readonly cadence: string;
  /** `false` is „wstrzymana". An empty cell in it is refused; a ticked one is not — `cellRefusal`. */
  readonly active: boolean;
  /** Every customer id the wire listed, including people this matrix has no row for. */
  readonly assigned: ReadonlySet<number>;
}

/** What a footer cell says, and what a cell's own label counts against. */
export interface MatrixTally {
  readonly held: number;
  readonly total: number;
}

export interface MatrixView {
  readonly rows: readonly MatrixRow[];
  readonly columns: readonly MatrixColumn[];
}

/**
 * The grid.
 *
 * The rows are the roster in the locale's own collation — the same comparator `surveyGroups` uses on
 * the survey titles, so „Ćwiczenia" and „Ćwikła" both sort where a Polish reader expects them. The
 * columns are the check-ins in the order the Ankiety tab already put them, because two tabs of one
 * screen listing one trainer's surveys in two different orders is a screen that cannot be scanned.
 */
export function buildMatrix(
  groups: SurveyGroups,
  faces: ReadonlyMap<number, ClientFace>,
  deps: { readonly t: TranslateKey; readonly locale: string },
): MatrixView {
  const { t, locale } = deps;
  const collator = new Intl.Collator(locale);

  const rows: MatrixRow[] = [...faces.values()]
    .map((face) => ({
      customerId: face.customerId,
      name: face.name,
      initials: face.initials,
      avatarUrl: face.avatarUrl,
    }))
    .sort((left, right) => collator.compare(left.name, right.name));

  const columns = groups.checkIns.map((row) => ({
    id: row.id,
    title: row.title,
    cadence: columnCadence(row, t),
    active: row.active,
    assigned: new Set(row.assignedCustomerIds),
  }));

  return { rows, columns };
}

/**
 * The second line of a column head.
 *
 * „wstrzymana" is appended to the rhythm rather than replacing it, because a paused survey still has
 * one and the trainer is about to resume it. A row whose rhythm the wire did not carry says only
 * that it is paused — which is the one fact it does have.
 */
function columnCadence(row: SurveyRow, t: TranslateKey): string {
  const cadence = cadenceLabel(row, t);
  if (row.active) return cadence ?? '';
  return cadence === null
    ? t('web:reports.matrix.pausedOnly')
    : t('web:reports.matrix.cadencePaused', { cadence });
}

function tally(assigned: ReadonlySet<number>, rows: readonly MatrixRow[]): MatrixTally {
  return {
    held: rows.filter((row) => assigned.has(row.customerId)).length,
    total: rows.length,
  };
}

/** A column's „n z N", counted against the rows this matrix actually draws. See the header. */
export function columnTally(column: MatrixColumn, view: MatrixView): MatrixTally {
  return tally(column.assigned, view.rows);
}

/** Whether this client holds this survey — the one question a cell's checkbox answers. */
export function holdsSurvey(column: MatrixColumn, customerId: number): boolean {
  return column.assigned.has(customerId);
}

/** Why a cell refuses the act it would otherwise send. One value so far; see the header. */
export type CellRefusal = 'pausedAssign';

/**
 * Whether this cell may be pressed at all, and why not.
 *
 * Asked before the request rather than after it, because the answer is knowable from the column the
 * trainer is looking at: the backend refuses an assignment into a paused check-in and takes an
 * unassignment from one. A cell that sends a write it knows will be refused spends a round trip to
 * put a red sentence where an explanation belonged.
 */
export function cellRefusal(column: MatrixColumn, customerId: number): CellRefusal | null {
  return !column.active && !holdsSurvey(column, customerId) ? 'pausedAssign' : null;
}

/** The refusal as a sentence the trainer reads. `t` arrives as a parameter, as everywhere here. */
export function cellRefusalMessage(refusal: CellRefusal, t: TranslateKey): string {
  return refusal === 'pausedAssign' ? t('web:reports.matrix.pausedCell') : '';
}
